import { RootState, AppDispatch } from "./store"; // Import AppDispatch
import Header from "./components/Header";
import { useRef, useEffect, useState } from "react"; // Added useEffect and useState
import Sidebar from "./components/Sidebar";
import { toggleSidebar } from "./store/sidebarSlice";
import { useDispatch, useSelector } from "react-redux";
import EditorPreview, { EditorPreviewRef } from "./components/EditorPreview";
import SocialShareButtons from "./components/SocialShareButtons"; // Import SocialShareButtons
import "./utils/i18n"; // Initialize i18n
import { useTranslation } from "react-i18next"; // Ajout import useTranslation
import { loadDefaultGpxActivity } from "./utils/defaultGpxLoader";
import { handleFileUpload } from "./utils/fileUpload";
import { addActivity, setActiveActivityIds } from "./store/activitiesSlice";
import { initializePoints } from "./store/pointsSlice";
import { setLayoutState } from "./store/layoutSlice";
import { setLabelsState } from "./store/labelsSlice";
import { setPointsState } from "./store/pointsSlice";
import { setMapState } from "./store/mapSlice";
import { setTraceState } from "./store/traceSlice";
import { setProfileState } from "./store/profileSlice";
import defaultTemplate from "./templates/defaultTemplate";
import { RxCross1 } from "react-icons/rx";
import { useLocation } from 'react-router-dom';

const App = () => {
  const dispatch: AppDispatch = useDispatch();
  const isSidebarOpen = useSelector((state: RootState) => state.sidebar.open);
  const checkout = useSelector((state: RootState) => state.checkout); // Get checkout state
  const activities = useSelector(
    (state: RootState) => state.activities.activities
  );
  const editorPreviewRef = useRef<EditorPreviewRef | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const { t } = useTranslation(); // Ajout hook useTranslation
  const location = useLocation();

  const handleToggleSidebar = () => {
    dispatch(toggleSidebar());
  };

  // Effect to potentially reset checkout state when navigating away from success state
  useEffect(() => {
    // If payment was successful but export hasn't triggered (e.g., user navigated away)
    // or if payment failed, reset the state so they can try again or edit.
    // This depends on the desired UX flow.
    // For simplicity, let's assume EditorPreview handles the export trigger.
    // We might reset the checkout state when the component unmounts (in Checkout.tsx) or here based on navigation.
    // Let's keep it simple: EditorPreview triggers export on success flag.
    // We don't need extra logic here for now.
  }, [checkout.paymentSucceeded]);

  // Bloquer le zoom navigateur globalement (Ctrl + roulette souris)
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey) {
        e.preventDefault();
      }
    };
    window.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      window.removeEventListener("wheel", handleWheel);
    };
  }, []);

  useEffect(() => {
    // Si aucune activité, charger le GPX par défaut
    if (activities.length === 0) {
      (async () => {
        const gpxText = await loadDefaultGpxActivity();
        if (gpxText) {
          const file = new File([gpxText], "paris-marathon.gpx", {
            type: "application/gpx+xml",
          });
          // Simuler un événement de changement de fichier pour réutiliser handleFileUpload
          const fakeEvent = {
            target: { files: [file] as unknown as FileList },
          } as React.ChangeEvent<HTMLInputElement>;
          // Utiliser un tableau vide pour existingActivities car nous sommes au démarrage de l'app
          const newActivities = await handleFileUpload(fakeEvent, []);
          if (newActivities.length > 0) {
            dispatch(addActivity(newActivities));
            dispatch(initializePoints(newActivities));
            dispatch(setActiveActivityIds(newActivities.map((a) => a.id)));
            // Correction : mettre à jour la stat Distance avec la vraie valeur
            const distance = newActivities[0]?.distance;
            if (typeof distance === "number" && isFinite(distance)) {
              const formatted = `${(distance / 1000).toFixed(2)} KM`;
              dispatch({
                type: "labels/updateStat",
                payload: { index: 1, label: "Distance", value: formatted },
              });
            }
          }
        }
      })();
    }
  }, [dispatch, activities]); // Déclencher si dispatch ou activities change

  useEffect(() => {
    // Si aucune activité et première visite (pas déjà initialisé)
    if (
      activities.length === 0 &&
      !localStorage.getItem("templateInitialized")
    ) {
      // Appliquer le template par défaut
      dispatch(setLayoutState(defaultTemplate.layout));
      const mergedLabels = {
        title: {
          text: defaultTemplate.labels?.title?.text || "",
          isVisible: defaultTemplate.labels?.title?.isVisible ?? true,
          style: defaultTemplate.labels?.title?.style || {},
        },
        description: {
          text: defaultTemplate.labels?.description?.text || "",
          isVisible: defaultTemplate.labels?.description?.isVisible ?? true,
          style: defaultTemplate.labels?.description?.style || {},
        },
        stats: Array.isArray(defaultTemplate.labels?.stats)
          ? defaultTemplate.labels.stats.map((stat) => ({
              label: stat.label || "",
              value: stat.value || "",
              style: stat.style || {},
            }))
          : [],
      };
      dispatch(setLabelsState(mergedLabels));
      dispatch(setTraceState(defaultTemplate.trace));
      dispatch(setPointsState(defaultTemplate.points));
      dispatch(setMapState(defaultTemplate.map));
      dispatch(setProfileState(defaultTemplate.profile));
      localStorage.setItem("templateInitialized", "1");
    }
  }, [dispatch, activities]);

  return (
    <>
      <div
        className={`flex flex-col w-full min-h-screen bg-[#111]`}
      >
        <Header
          toggleSidebar={handleToggleSidebar}
          isSidebarOpen={isSidebarOpen}
        />
        <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
          {" "}
          {/* Main content area */}
          {/* Sidebar - Conditionally rendered based on route? No, kept always for editor */}
          <Sidebar
            isSidebarOpen={isSidebarOpen}
            editorPreviewRef={editorPreviewRef}
            showBanner={showBanner} // Pass showBanner prop
          />
          {/* Main Editor Preview Area */}
          <main className="flex-auto flex flex-col h-full w-full min-h-0 min-w-0 overflow-hidden">
            {" "}
            {/* Ensure main takes remaining space */}
            <div className="flex-auto flex flex-col h-full w-full items-center relative z-10 overflow-auto min-h-0 min-w-0 scrollbar-thin scrollbar-thumb-[#333] scrollbar-track-[#222] p-5">
              {" "}
              {/* Added padding */}
              <div className="flex-none m-auto">
                {" "}
                {/* Center the preview */}
                <EditorPreview ref={editorPreviewRef} />
              </div>
            </div>
          </main>
        </div>
        <SocialShareButtons /> {/* Add SocialShareButtons here */}
      </div>
    </>
  );
};

export default App;
